import { recordChanged } from "common/helpers/Record";
import { Nullable } from "common/records";
import { CustomFormRecord } from "common/records/CustomForm";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { List, Map } from "immutable";
import { createSelector } from "reselect";

const rootKey = "customForms";

/**
 * Loads byId key from customForms.
 *
 * @param {RootState} state The RootState
 * @returns {Nullable<Map<string | -1, CustomFormRecord>>} Map containing all custom forms in state by ID with any unsaved changes applied.
 */
export const byId = (state: RootState): Nullable<Map<string | -1, CustomFormRecord>> => state.getIn([rootKey, "byId"]);

/**
 * Loads lastSavedById key from customForms (represents what's saved on the
 * server).
 *
 * @param {RootState} state The RootState
 * @returns {Nullable<Map<string | -1, CustomFormRecord>>} Map containing all custom forms in state as they exist on the server.
 */
export const lastSavedById = (state: RootState): Nullable<Map<string | -1, CustomFormRecord>> =>
  state.getIn([rootKey, "lastSavedById"]);

/**
 * Loads the custom form by ID.
 *
 * @param {RootState} state The RootState
 * @param {{ id: string | -1 }} props The custom form ID
 * @returns {Nullable<CustomFormRecord>} Current representation of the custom form in state
 */
export const customForm = (state: RootState, props: { id: string | -1 | undefined }): Nullable<CustomFormRecord> => {
  if (props.id === undefined) {
    return null;
  }

  return byId(state)?.get(props.id);
};

/**
 * Loads last saved by ID key from customForms (represents what's saved on the
 * server).
 *
 * @param {RootState} state The RootState
 * @param {{ id: string | -1 }} props The custom form ID
 * @returns {Nullable<CustomFormRecord>} The last saved version of the custom form in state
 */
export const lastSavedCustomForm = (state: RootState, props: { id: string | -1 }): Nullable<CustomFormRecord> =>
  lastSavedById(state)?.get(props.id);

/**
 * Gets the first job resulting custom form.
 *
 * @param {RootState} state The RootState
 * @returns {Nullable<CustomFormRecord>} Loads the first ID for custom forms
 */
export const jobResultingCustomForm = (state: RootState): Nullable<CustomFormRecord> => {
  const customFormIds = ids(state, { modelName: rootKey }) || List();
  const jobResultingFormId = (customFormIds.first() || "") as string;

  return byId(state)?.get(jobResultingFormId);
};

/**
 * Get the result of the job resulting custom form.
 *
 * @param {RootState} state The RootState
 * @returns {Nullable<string>} The result of the job
 */
export const jobResult = createSelector([jobResultingCustomForm], (customForm): Nullable<string> => {
  return customForm?.getIn(["form_data", "result"]);
});

/**
 * Did the custom form change?
 *
 * @param {RootState} state The RootState
 * @param {{ id: string | -1 }} props The custom form ID
 * @returns {boolean}
 */
export const customFormChanged = (state: RootState, props: { id: string | -1 }) => {
  const customFormRecord = customForm(state, props);
  const lastSavedCustomFormRecord = lastSavedCustomForm(state, props);
  if (!customFormRecord || !lastSavedCustomFormRecord) {
    return false;
  }

  return recordChanged(customFormRecord, lastSavedCustomFormRecord);
};
